import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EventoService from '../../services/eventoService';
import NotificacionService from '../../services/notificacionService';
import CrearEventoModal from '../../components/CrearEventoModal';
import BotonCancelarReactivarTramite from '../../components/Tramite/BotonCancelarReactivarTramite';
import ChecklistTramite from '../../components/Tramite/ChecklistTramite';
import TarjetaCobroTramite from '../../components/Cobranzas/TarjetaCobroTramite';
import SeccionCobranzaTramite from '../../components/Cobranzas/SeccionCobranzaTramite';
import HistorialTramite from '../../components/HistorialTramite';
import PanelAccionesAvanzadasTramite from '../../components/Administracion/PanelAccionesAvanzadasTramite';
import BannerEstadoFinal from '../../components/BannerEstadoFinal';
import WhatsAppHeaderButton from '../../components/WhatsAppHeaderButton';
import PanelUbicacionTramite from '../../components/PanelUbicacionTramite';
import FeedbackButton from '../../components/FeedbackButton';
import NotaComentario from '../../components/NotaComentario';
import BotonGenerarEscritoAmparo from '../../components/Amparo/BotonGenerarEscritoAmparo';
import ComboboxDatosMaestros from '../../components/ComboboxDatosMaestros';
import DatosMaestrosService from '../../services/datosMaestrosService';
import useCustomModal from '../../hooks/useCustomModal';
import useVolverAtras from '../../hooks/useVolverAtras';
import CustomModal from '../../components/CustomModal';
import { useAuth } from '../../auth/AuthContext';
import {
    getTramiteAmparoById, actualizarSeguimientoAmparo, actualizarComentariosAmparo,
    avanzarEstadoAmparo, actualizarChecklistAmparo,
} from '../../services/amparoService';
import { subirArchivo, descargarArchivo, obtenerArchivosPorTramite, eliminarArchivo } from '../../services/archivoService';
import { apellidoNombre } from '../../utils/nombrePersona';
import { rutaDetalleTramite } from '../../utils/rutasTramite';
import EtiquetaTramite from '../../components/EtiquetaTramite';

const COLOR = '#be123c';
const COLOR_OSCURO = '#9f1239';
const COLOR_FONDO = '#ffe4e6';

const formatearFechaEvento = (fechaString) => {
    if (!fechaString) return '';
    const [y, m, d] = fechaString.split('T')[0].split('-');
    return `${d}/${m}/${y}`;
};

// Mini-formulario por transición (patrón Declaratoria/Ganancias/Minusvalía).
// CERRADO_DESFAVORABLE está disponible desde cualquier estado posterior al ingreso.
const TRANSICION_DESFAVORABLE = {
    destino: 'CERRADO_DESFAVORABLE',
    label: 'Cerrar desfavorable',
    icon: 'bi-x-octagon',
    variant: 'outline-secondary',
    campos: [
        { name: 'motivoDesfavorable', label: 'Motivo (por qué no prosperó)', type: 'textarea' },
    ],
};

const TRANSICION_CANCELAR = {
    destino: 'CANCELADO',
    label: 'Cancelar trámite',
    icon: 'bi-slash-circle-fill',
    variant: 'outline-secondary',
    campos: [
        { name: 'comentario', label: 'Motivo de la cancelación', type: 'textarea' },
    ],
};

const TRANSICION_REACTIVAR = {
    destino: 'INGRESO',
    label: 'Reactivar trámite',
    icon: 'bi-arrow-counterclockwise',
    variant: 'outline-primary',
    campos: [
        { name: 'comentario', label: 'Motivo de la reactivación', type: 'textarea' },
    ],
};

const TRANSICIONES_POR_ESTADO = {
    'Ingreso del Trámite': [
        {
            destino: 'PRONTO_DESPACHO', label: 'Registrar pronto despacho', icon: 'bi-envelope-paper', variant: 'primary',
            campos: [
                { name: 'fechaProntoDespacho', label: 'Fecha de envío del telegrama (Ley 23.789)', type: 'date' },
                { name: 'nroTelegrama', label: 'N° de telegrama (opcional)', type: 'text' },
                { name: 'fechaAcuse', label: 'Fecha del acuse de recibo (opcional)', type: 'date' },
                { name: 'acuseRecibido', label: 'Ya se recibió el acuse', type: 'checkbox' },
            ],
        },
        TRANSICION_CANCELAR,
    ],
    'Pronto despacho enviado': [
        {
            destino: 'AMPARO_PRESENTADO', label: 'Registrar presentación del amparo', icon: 'bi-send-check', variant: 'primary',
            campos: [
                { name: 'fechaAmparoPresentado', label: 'Fecha de presentación', type: 'date' },
                { name: 'juzgado', label: 'Juzgado', type: 'lugar', tipoLugar: 'Juzgado' },
                { name: 'nroExpedienteJudicial', label: 'N° de expediente judicial (opcional)', type: 'text' },
                { name: 'caratula', label: 'Carátula (opcional, se sugiere sola)', type: 'text' },
            ],
        },
        TRANSICION_DESFAVORABLE,
        TRANSICION_CANCELAR,
    ],
    'Amparo presentado': [
        {
            destino: 'INTIMACION', label: 'Registrar intimación a ANSES', icon: 'bi-hourglass-split', variant: 'primary',
            campos: [
                { name: 'fechaIntimacion', label: 'Fecha de la intimación', type: 'date' },
            ],
            ayuda: 'El vencimiento de los 5 días hábiles judiciales se calcula solo (salteando fines de semana '
                + 'y días inhábiles de Justicia Córdoba) y queda como evento en la agenda.',
        },
        TRANSICION_DESFAVORABLE,
        TRANSICION_CANCELAR,
    ],
    'Intimación a ANSES': [
        {
            destino: 'PLAZO_FIJADO', label: 'Registrar plazo fijado', icon: 'bi-calendar-check', variant: 'primary',
            campos: [
                { name: 'fechaLimiteResolucion', label: 'Fecha límite para que ANSES resuelva', type: 'date' },
                { name: 'fechaPlazoFijado', label: 'Fecha de la resolución que fija el plazo (opcional)', type: 'date' },
            ],
            ayuda: 'Se crea un evento de vencimiento en la fecha límite, con aviso 3 días antes.',
        },
        TRANSICION_DESFAVORABLE,
        TRANSICION_CANCELAR,
    ],
    'Plazo fijado': [
        {
            destino: 'CERRADO_FAVORABLE', label: 'Cerrar favorable (ANSES resolvió)', icon: 'bi-check-circle-fill', variant: 'success',
            campos: [
                { name: 'fechaResolucion', label: 'Fecha de la resolución de ANSES', type: 'date' },
                { name: 'nroResolucion', label: 'N° de resolución (opcional)', type: 'text' },
            ],
        },
        TRANSICION_DESFAVORABLE,
        TRANSICION_CANCELAR,
    ],
    'Cancelado': [TRANSICION_REACTIVAR],
};

function MiniFormularioTransicion({ idTramite, transicion, onGuardado }) {
    const [abierto, setAbierto] = useState(false);
    const [valores, setValores] = useState({});
    const [guardando, setGuardando] = useState(false);
    const [lugares, setLugares] = useState([]);

    const tieneCampoLugar = transicion.campos.some(c => c.type === 'lugar');

    // Carga el catálogo de Lugares solo si esta transición tiene un campo conectado a él (el
    // juzgado): ComboboxDatosMaestros sugiere los que ya existen y crea uno nuevo al vuelo.
    useEffect(() => {
        if (!abierto || !tieneCampoLugar) return;
        DatosMaestrosService.getLugares()
            .then((resp) => setLugares(resp.data || []))
            .catch((err) => console.error('Error al cargar lugares:', err));
    }, [abierto, tieneCampoLugar]);

    const handleChange = (name, value) => setValores(prev => ({ ...prev, [name]: value }));

    // Si el operador tipeó un juzgado nuevo y confirmó sin que el combobox llegara a crearlo
    // (onBlur no llegó a dispararse), lo crea acá antes de avanzar de estado — mismo respaldo que
    // Declaratoria/Minusvalía.
    const crearLugaresFaltantes = async () => {
        for (const campo of transicion.campos) {
            if (campo.type !== 'lugar') continue;
            const nombreTrim = (valores[campo.name] || '').trim();
            if (!nombreTrim) continue;
            const yaExiste = lugares.some(l => (l.nombre || '').trim().toLowerCase() === nombreTrim.toLowerCase());
            if (yaExiste) continue;
            try {
                await DatosMaestrosService.createLugar({
                    nombre: nombreTrim,
                    tipo: campo.tipoLugar || 'Oficina',
                    direccion: '',
                    telefono: '',
                    descripcion: 'Creado automáticamente desde trámite de Amparo por mora',
                });
            } catch (err) {
                console.error('Error al crear lugar automáticamente:', err);
            }
        }
    };

    const guardar = async () => {
        setGuardando(true);
        try {
            if (tieneCampoLugar) {
                await crearLugaresFaltantes();
            }
            const payload = { destino: transicion.destino, ...valores };
            const actualizado = await avanzarEstadoAmparo(idTramite, payload);
            onGuardado(actualizado);
            toast.success('Estado actualizado correctamente');
            setAbierto(false);
            setValores({});
        } catch (err) {
            console.error('Error al avanzar de estado:', err);
            toast.error(err.response?.data || 'No se pudo avanzar de estado');
        } finally {
            setGuardando(false);
        }
    };

    if (!abierto) {
        return (
            <button className={`btn btn-${transicion.variant || 'primary'} d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold`} onClick={() => setAbierto(true)}>
                <i className={`bi ${transicion.icon || 'bi-arrow-right-circle'}`}></i>{transicion.label}
            </button>
        );
    }

    return (
        <div className="p-4 border rounded-3 bg-light w-100">
            <h5 className="fw-semibold mb-3" style={{ color: COLOR_OSCURO }}>
                <i className={`bi ${transicion.icon || 'bi-arrow-right-circle'} me-2`}></i>{transicion.label}
            </h5>
            {transicion.ayuda && (
                <div className="alert alert-info border-0 rounded-3 py-2 px-3 small">
                    <i className="bi bi-info-circle me-2"></i>{transicion.ayuda}
                </div>
            )}
            <div className="row g-3">
                {transicion.campos.map(campo => (
                    <div key={campo.name} className={campo.type === 'textarea' ? 'col-12' : 'col-md-6'}>
                        {campo.type === 'checkbox' ? (
                            <div className="form-check mt-4">
                                <input className="form-check-input" type="checkbox" id={`chk-${campo.name}`}
                                    checked={!!valores[campo.name]}
                                    onChange={(e) => handleChange(campo.name, e.target.checked)} />
                                <label className="form-check-label fw-semibold" htmlFor={`chk-${campo.name}`}>{campo.label}</label>
                            </div>
                        ) : (
                            <>
                                <label className="form-label fw-semibold">{campo.label}</label>
                                {campo.type === 'textarea' ? (
                                    <textarea className="form-control shadow-sm" rows={3}
                                        value={valores[campo.name] || ''} onChange={(e) => handleChange(campo.name, e.target.value)} />
                                ) : campo.type === 'lugar' ? (
                                    <ComboboxDatosMaestros
                                        opciones={lugares.filter(l => l.tipo === campo.tipoLugar || !l.tipo)}
                                        valor={valores[campo.name] || ''}
                                        onChange={(valor) => handleChange(campo.name, valor)}
                                        tipo="lugar"
                                        tipoLugar={campo.tipoLugar}
                                        placeholder="Seleccione o escriba..."
                                    />
                                ) : (
                                    <input type={campo.type} className="form-control shadow-sm"
                                        value={valores[campo.name] || ''} onChange={(e) => handleChange(campo.name, e.target.value)} />
                                )}
                            </>
                        )}
                    </div>
                ))}
            </div>
            <div className="d-flex justify-content-center gap-3 mt-4">
                <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={guardar} disabled={guardando}
                    style={{ background: COLOR, border: 'none' }}>
                    {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle-fill"></i>Confirmar</>}
                </button>
                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                    onClick={() => { setAbierto(false); setValores({}); }} disabled={guardando}>
                    Cancelar
                </button>
            </div>
        </div>
    );
}

function SeccionAvanzarEstado({ idTramite, nombreEstadoActual, onAvanzado }) {
    const transiciones = TRANSICIONES_POR_ESTADO[nombreEstadoActual] || [];
    if (transiciones.length === 0) {
        return (
            <div className="alert alert-secondary border-0 rounded-3 mb-0 d-flex align-items-center gap-2">
                <i className="bi bi-flag-fill"></i>Este trámite está en una etapa final del ciclo de estados.
            </div>
        );
    }
    return (
        <div className="d-flex flex-wrap gap-3">
            {transiciones.map(t => (
                <MiniFormularioTransicion key={t.destino} idTramite={idTramite} transicion={t} onGuardado={onAvanzado} />
            ))}
        </div>
    );
}

// Los 5 pasos del ciclo, para el stepper. Los cierres alternativos (desfavorable, cancelado) no son
// pasos: son salidas, y las muestra BannerEstadoFinal.
const PASOS = ['Ingreso del Trámite', 'Pronto despacho enviado', 'Amparo presentado', 'Intimación a ANSES', 'Plazo fijado'];

function StepperAmparo({ nombreEstadoActual }) {
    const indiceActual = PASOS.indexOf(nombreEstadoActual);
    const cerradoFavorable = nombreEstadoActual === 'Cerrado favorable';
    return (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            {PASOS.map((paso, i) => {
                const hecho = cerradoFavorable || (indiceActual >= 0 && i < indiceActual);
                const actual = i === indiceActual;
                const color = hecho ? '#16a34a' : actual ? COLOR : '#cbd5e1';
                return (
                    <div key={paso} className="d-flex align-items-center gap-2">
                        <span className="badge rounded-pill px-3 py-2" style={{
                            backgroundColor: actual ? COLOR_FONDO : 'transparent',
                            color, border: `1px solid ${color}`, fontWeight: actual ? 600 : 400,
                        }}>
                            <i className={`bi ${hecho ? 'bi-check-circle-fill' : actual ? 'bi-record-circle' : 'bi-circle'} me-1`}></i>
                            {paso}
                        </span>
                        {i < PASOS.length - 1 && <i className="bi bi-chevron-right text-muted small"></i>}
                    </div>
                );
            })}
            {cerradoFavorable && (
                <span className="badge rounded-pill px-3 py-2" style={{ backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #16a34a', fontWeight: 600 }}>
                    <i className="bi bi-check-circle-fill me-1"></i>Cerrado favorable
                </span>
            )}
        </div>
    );
}

/** Contador de plazo: días restantes, en rojo si ya venció. Mismo criterio que el del BOE en Declaratoria. */
function ContadorPlazo({ titulo, fecha, diasRestantes, descripcion }) {
    if (!fecha) {
        return (
            <div className="col-md-6">
                <div className="border rounded-3 p-3 h-100 bg-light">
                    <div className="fw-semibold text-muted"><i className="bi bi-hourglass me-2"></i>{titulo}</div>
                    <div className="small text-muted mt-1">Todavía no corre.</div>
                </div>
            </div>
        );
    }
    const color = diasRestantes == null ? 'secondary' : diasRestantes < 0 ? 'danger' : diasRestantes <= 2 ? 'warning' : 'success';
    return (
        <div className="col-md-6">
            <div className={`border rounded-3 p-3 h-100 border-${color}`} style={{ borderWidth: '2px' }}>
                <div className="fw-semibold" style={{ color: COLOR_OSCURO }}><i className="bi bi-hourglass-split me-2"></i>{titulo}</div>
                <div className="mt-1">Vence el <strong>{fecha}</strong></div>
                {diasRestantes != null && (
                    <div className={`mt-1 fw-semibold text-${color}`}>
                        {diasRestantes < 0
                            ? `Venció hace ${Math.abs(diasRestantes)} ${Math.abs(diasRestantes) === 1 ? 'día' : 'días'}`
                            : diasRestantes === 0
                                ? 'Vence hoy'
                                : `Faltan ${diasRestantes} ${diasRestantes === 1 ? 'día' : 'días'}`}
                    </div>
                )}
                {descripcion && <div className="small text-muted mt-1">{descripcion}</div>}
            </div>
        </div>
    );
}

function VistaDetalladaTramiteAmparo() {
    const { idTramite } = useParams();
    const navigate = useNavigate();
    const volverAtras = useVolverAtras('/tramites');
    const { user } = useAuth();
    const { modalState, hideModal, showModal } = useCustomModal();

    const [tramite, setTramite] = useState(null);
    const [loading, setLoading] = useState(true);

    const [editSeguimiento, setEditSeguimiento] = useState(false);
    const [seguimientoEdit, setSeguimientoEdit] = useState(null);
    const [guardandoSeguimiento, setGuardandoSeguimiento] = useState(false);

    const [eventos, setEventos] = useState([]);
    const [cargandoEventos, setCargandoEventos] = useState(true);
    const [mostrarModalEvento, setMostrarModalEvento] = useState(false);
    const [fechaSeleccionadaEvento, setFechaSeleccionadaEvento] = useState(new Date());

    const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);

    const [archivos, setArchivos] = useState([]);
    const [subiendoGeneral, setSubiendoGeneral] = useState(false);
    const [subiendoItem, setSubiendoItem] = useState(null);

    const recargarTramite = useCallback(async () => {
        try {
            const data = await getTramiteAmparoById(idTramite);
            setTramite(data);
        } catch (error) {
            console.error('Error al cargar trámite de Amparo:', error);
        }
    }, [idTramite]);

    const recargarArchivos = useCallback(async () => {
        try {
            const data = await obtenerArchivosPorTramite(idTramite);
            setArchivos(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar archivos:', error);
        }
    }, [idTramite]);

    const recargarEventos = useCallback(async () => {
        try {
            const response = await EventoService.getEventosPorTramite(idTramite);
            setEventos(response.data || []);
        } catch (error) {
            console.error('Error recargando eventos:', error);
        }
    }, [idTramite]);

    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            setCargandoEventos(true);
            try {
                const [tramiteResult, eventosResult, archivosResult] = await Promise.allSettled([
                    getTramiteAmparoById(idTramite),
                    EventoService.getEventosPorTramite(idTramite),
                    obtenerArchivosPorTramite(idTramite),
                ]);
                setTramite(tramiteResult.status === 'fulfilled' ? tramiteResult.value : null);
                setEventos(eventosResult.status === 'fulfilled' ? (eventosResult.value.data || []) : []);
                setArchivos(archivosResult.status === 'fulfilled' && Array.isArray(archivosResult.value) ? archivosResult.value : []);
            } finally {
                setLoading(false);
                setCargandoEventos(false);
            }
        };
        cargar();
    }, [idTramite]);

    useEffect(() => {
        const handleEventoCreado = () => recargarEventos();
        window.addEventListener('eventoCreadoDesdeChat', handleEventoCreado);
        const interval = setInterval(() => recargarEventos(), 30000);
        return () => {
            window.removeEventListener('eventoCreadoDesdeChat', handleEventoCreado);
            clearInterval(interval);
        };
    }, [recargarEventos]);

    const formatLocalFecha = (fechaInput) => {
        if (!fechaInput) return '';
        if (typeof fechaInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaInput)) return fechaInput;
        const d = new Date(fechaInput);
        if (Number.isNaN(d.getTime())) return '';
        return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
    };

    const handleGuardarEvento = async (evento) => {
        try {
            const responseEvento = await EventoService.crearEvento({
                ...evento,
                fecha: formatLocalFecha(evento.fecha),
                idTramite: Number(idTramite),
            });
            const idEventoCreado = responseEvento.data.idEvento || responseEvento.data;
            if (evento.tiemposRecordatorio && evento.tiemposRecordatorio.length > 0) {
                await NotificacionService.programarNotificacionesAutomaticas(idEventoCreado, evento.tiemposRecordatorio);
            }
            window.dispatchEvent(new CustomEvent('eventoCreadoDesdeChat', { detail: { timestamp: Date.now(), source: 'tramite-amparo' } }));
            await recargarEventos();
            setMostrarModalEvento(false);
            toast.success('Evento creado correctamente');
        } catch (error) {
            console.error('Error al crear evento:', error);
            toast.error('Error al crear el evento');
        }
    };

    const descargarPorId = async (idArchivo) => {
        try {
            const meta = archivos.find(a => a.idArchivo === idArchivo);
            const nombre = meta?.nombre || `archivo_${idArchivo}`;
            const resp = await descargarArchivo(idArchivo);
            const blob = meta?.tipo ? new Blob([resp.data], { type: meta.tipo }) : new Blob([resp.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = nombre;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error al descargar archivo:', err);
            toast.error('No se pudo descargar el archivo');
        }
    };

    const subirGeneral = async (file) => {
        if (!file) return;
        setSubiendoGeneral(true);
        try {
            await subirArchivo(idTramite, file);
            await recargarArchivos();
            toast.success('Archivo subido');
        } catch (err) {
            console.error('Error al subir archivo:', err);
            toast.error('No se pudo subir el archivo');
        } finally {
            setSubiendoGeneral(false);
        }
    };

    const eliminarGeneral = (idArchivo) => {
        showModal({
            title: 'Eliminar archivo',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: '¿Seguro que querés eliminar este archivo? Esta acción no se puede deshacer.',
            onConfirm: async () => {
                try {
                    await eliminarArchivo(idArchivo);
                    await recargarArchivos();
                    toast.info('Archivo eliminado');
                } catch (err) {
                    console.error('Error al eliminar archivo:', err);
                    toast.error('No se pudo eliminar el archivo');
                }
            },
        });
    };

    // ---- Checklist (formulario_catalogo) ----
    const togglePresentado = async (item) => {
        if (item.idArchivo) return;
        try {
            await actualizarChecklistAmparo(item.id, !item.presentado, null);
            recargarTramite();
        } catch (err) {
            console.error('Error al actualizar checklist:', err);
        }
    };
    const adjuntarArchivoChecklist = async (item, file) => {
        if (!file) return;
        setSubiendoItem(item.id);
        try {
            const resp = await subirArchivo(idTramite, file);
            const idArchivo = resp?.idArchivo ?? null;
            await actualizarChecklistAmparo(item.id, true, idArchivo);
            await Promise.all([recargarTramite(), recargarArchivos()]);
        } catch (err) {
            console.error('Error al adjuntar archivo:', err);
            toast.error('No se pudo adjuntar el archivo');
        } finally {
            setSubiendoItem(null);
        }
    };
    const quitarAdjuntoChecklist = (item) => {
        showModal({
            title: 'Eliminar documento',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: `¿Seguro que querés eliminar el archivo adjunto de "${item.nombre}"?`,
            onConfirm: async () => {
                try {
                    await eliminarArchivo(item.idArchivo);
                    await actualizarChecklistAmparo(item.id, false, null);
                    await Promise.all([recargarTramite(), recargarArchivos()]);
                    toast.info('Documento eliminado');
                } catch (err) {
                    console.error('Error al eliminar documento:', err);
                    toast.error('No se pudo eliminar el documento');
                }
            },
        });
    };

    const handleGuardarComentario = async (nuevoComentario) => {
        try {
            await actualizarComentariosAmparo(idTramite, nuevoComentario);
            await recargarTramite();
            toast.success('Comentario actualizado correctamente');
        } catch (error) {
            console.error('Error al guardar comentario:', error);
            toast.error('No se pudo guardar el comentario');
            throw error;
        }
    };

    const handleModificarSeguimiento = () => {
        setSeguimientoEdit({
            nroExpedienteAnses: tramite.nroExpedienteAnses || '',
            udai: tramite.udai || '',
            fechaInicioTramiteAnses: tramite.fechaInicioTramiteAnses || '',
            fechaProntoDespacho: tramite.fechaProntoDespacho || '',
            nroTelegrama: tramite.nroTelegrama || '',
            acuseRecibido: !!tramite.acuseRecibido,
            fechaAcuse: tramite.fechaAcuse || '',
            respuestaOrganismo: tramite.respuestaOrganismo || '',
            juzgado: tramite.juzgado || '',
            caratula: tramite.caratula || '',
            nroExpedienteJudicial: tramite.nroExpedienteJudicial || '',
            fechaAmparoPresentado: tramite.fechaAmparoPresentado || '',
            fechaIntimacion: tramite.fechaIntimacion || '',
            fechaLimiteResolucion: tramite.fechaLimiteResolucion || '',
            tiempoComprometido: tramite.tiempoComprometido || '',
        });
        setEditSeguimiento(true);
    };
    const handleGuardarSeguimiento = async () => {
        setGuardandoSeguimiento(true);
        try {
            await actualizarSeguimientoAmparo(idTramite, seguimientoEdit);
            await recargarTramite();
            await recargarEventos();
            setEditSeguimiento(false);
            toast.success('Seguimiento actualizado');
        } catch (error) {
            console.error('Error al guardar el seguimiento:', error);
            toast.error(error.response?.data || 'No se pudo guardar el seguimiento');
        } finally {
            setGuardandoSeguimiento(false);
        }
    };

    const handleAvanzarEstado = (tramiteActualizado) => setTramite(tramiteActualizado);

    const verDetalleCliente = (cuil) => navigate(`/clientes/vista-detallada/${cuil.toString()}`);

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: COLOR }}></div>
                <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando trámite...</p>
            </div>
        );
    }
    if (!tramite) {
        return <div className="alert alert-danger mt-4">No se encontró el trámite.</div>;
    }

    const cliente = tramite.cliente || {};
    const formularios = tramite.formularios || [];
    const idsEnChecklist = new Set(formularios.map(f => f.idArchivo).filter(Boolean));
    const archivosOtros = archivos.filter(a => !idsEnChecklist.has(a.idArchivo));
    const nombrePorId = archivos.reduce((acc, a) => { acc[a.idArchivo] = a.nombre; return acc; }, {});
    const formatTamano = (bytes) => (bytes == null ? '' : (bytes / (1024 * 1024)).toFixed(2) + ' MB');

    // Los datos que el escrito necesita, leídos del trámite ya guardado: el botón los usa solo para
    // decidir qué avisos mostrar (el documento lo arma el backend desde la entidad).
    const datosEscrito = {
        cuil: cliente.cuil,
        subtipo: tramite.subtipo,
        nroExpedienteAnses: tramite.nroExpedienteAnses,
        tiempoComprometido: tramite.tiempoComprometido,
    };

    const rutaFondo = tramite.idTramiteFondo
        ? rutaDetalleTramite({ idTramite: tramite.idTramiteFondo, nombreTipoTramite: tramite.tipoTramiteFondo })
        : null;

    return (
        <div className="container mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
                    <div className="d-flex align-items-center flex-wrap gap-3 mb-1">
                        <h2 className="fw-bold mb-0" style={{ color: COLOR }}>Detalles del Trámite de Amparo por Mora</h2>
                        <span className="badge rounded-pill fs-6 px-3" style={{ backgroundColor: COLOR_FONDO, color: COLOR_OSCURO, border: '1px solid #fecdd3' }}>
                            AMPARO · ANSES
                        </span>
                        {tramite.subtipoEtiqueta && (
                            <span className="badge rounded-pill fs-6 px-3" style={{ backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}>
                                {tramite.subtipoEtiqueta}
                            </span>
                        )}
                        <EtiquetaTramite etiqueta={tramite.etiqueta} />
                    </div>
                    {cliente?.apellido && cliente?.nombre && (
                        <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                            <h5 className="text-secondary fw-semibold mb-0">
                                <PersonSearchIcon fontSize="small" className="me-1 mb-1" />
                                {apellidoNombre(cliente.apellido, cliente.nombre)}
                            </h5>
                            {cliente.telefonoPropio && (
                                <WhatsAppHeaderButton
                                    phone={cliente.telefonoPropio}
                                    clientName={`${cliente.nombre} ${cliente.apellido}`}
                                    onClick={() => window.dispatchEvent(new CustomEvent('openAdminSidebar', { detail: { tab: 'whatsapp' } }))}
                                />
                            )}
                        </div>
                    )}
                    {tramite.reclamaPorTercero && tramite.titularNombre && (
                        <div className="text-muted small mt-1">
                            <i className="bi bi-people me-1"></i>
                            Reclama por <strong>{tramite.titularNombre}</strong>
                            {tramite.vinculoTitular ? ` (${tramite.vinculoTitular})` : ''}
                            {tramite.titularCuil ? ` — ${tramite.titularCuil}` : ''}
                        </div>
                    )}
                    <PanelUbicacionTramite tramite={tramite} onUpdated={setTramite} />
                </div>
                <div className="d-flex gap-2 mt-1 align-items-center flex-wrap">
                    {/* Recarga los archivos además del trámite: el .docx generado queda adjuntado. */}
                    <BotonGenerarEscritoAmparo
                        idTramite={tramite.idTramite}
                        datos={datosEscrito}
                        onGenerado={() => Promise.all([recargarTramite(), recargarArchivos()])}
                    />
                    <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill" onClick={volverAtras}>
                        <ArrowBackIcon fontSize="small" /> Volver
                    </button>
                </div>
            </div>

            <BannerEstadoFinal idTramite={tramite.idTramite} nombreEstadoActual={tramite.nombreEstadoActual} />

            {/* Estado del ciclo */}
            <div className="card shadow-sm border-0 rounded-4 p-4 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div className="d-flex align-items-center gap-3">
                        <h5 className="fw-bold text-secondary mb-0">Estado</h5>
                        <span className="badge rounded-pill fs-6 px-3" style={{ backgroundColor: COLOR_FONDO, color: COLOR_OSCURO, border: '1px solid #fecdd3' }}>
                            {tramite.nombreEstadoActual || 'Ingreso del Trámite'}
                        </span>
                        {tramite.fechaEstadoActual && (
                            <span className="text-muted small">{new Date(tramite.fechaEstadoActual).toLocaleDateString('es-ES')}</span>
                        )}
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setModalHistorialAbierto(true)}>
                            <i className="bi bi-clock-history me-1"></i>Ver historial
                        </button>
                        <BotonCancelarReactivarTramite tramite={tramite} onActualizado={recargarTramite} />
                    </div>
                </div>

                <StepperAmparo nombreEstadoActual={tramite.nombreEstadoActual} />

                <SeccionAvanzarEstado idTramite={idTramite} nombreEstadoActual={tramite.nombreEstadoActual} onAvanzado={handleAvanzarEstado} />
            </div>

            {/* Contadores de plazo */}
            <div className="card shadow-sm border-0 rounded-4 p-4 mb-3">
                <h4 className="fw-bold mb-3" style={{ color: COLOR_OSCURO }}><i className="bi bi-stopwatch me-2"></i>Plazos</h4>
                <div className="row g-3">
                    <ContadorPlazo
                        titulo="Informe de ANSES (5 días hábiles judiciales)"
                        fecha={tramite.fechaVencimientoInforme}
                        diasRestantes={tramite.diasRestantesInforme}
                        descripcion={tramite.fechaIntimacion ? `Intimación del ${tramite.fechaIntimacion}.` : null}
                    />
                    <ContadorPlazo
                        titulo="Plazo perentorio para resolver"
                        fecha={tramite.fechaLimiteResolucion}
                        diasRestantes={tramite.diasRestantesResolucion}
                        descripcion={tramite.fechaPlazoFijado ? `Plazo fijado el ${tramite.fechaPlazoFijado}.` : null}
                    />
                </div>
            </div>

            <NotaComentario
                valor={tramite.comentarios || ''}
                onGuardar={handleGuardarComentario}
                label="Comentarios del Trámite de Amparo"
                color="blue"
                nombreUsuario={user?.nombre || ''}
            />

            {/* Cobranza del trámite: desplegable, debajo del estado y los comentarios */}
            <SeccionCobranzaTramite tramite={tramite} />

            {/* Trámite de fondo + Cliente */}
            <div className="row g-4 mt-1">
                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold mb-3" style={{ color: COLOR_OSCURO }}><i className="bi bi-folder2-open me-2"></i>Trámite de fondo</h4>
                        {tramite.idTramiteFondo ? (
                            <>
                                <p><strong>Tipo:</strong> {tramite.tipoTramiteFondo || 'N/A'}</p>
                                <p><strong>Estado actual:</strong> {tramite.estadoTramiteFondo || 'N/A'}</p>
                                {tramite.etiquetaTramiteFondo && <p><strong>Etiqueta:</strong> {tramite.etiquetaTramiteFondo}</p>}
                                <p><strong>Expediente ANSES:</strong> {tramite.nroExpedienteAnses || 'N/A'}</p>
                                <p><strong>UDAI:</strong> {tramite.udai || 'N/A'}</p>
                                <p><strong>Ingresó el:</strong> {tramite.fechaInicioTramiteAnses || 'N/A'}</p>
                                {rutaFondo && (
                                    <div className="mt-2 d-flex justify-content-center">
                                        <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                            onClick={() => navigate(rutaFondo)}>
                                            <i className="bi bi-box-arrow-up-right"></i> Ver el trámite de fondo
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="alert alert-secondary border-0 rounded-3 mb-2">
                                    <i className="bi bi-info-circle me-2"></i>
                                    Este amparo no está vinculado a ningún trámite del sistema.
                                </div>
                                <p><strong>Expediente ANSES:</strong> {tramite.nroExpedienteAnses || 'N/A'}</p>
                                <p><strong>UDAI:</strong> {tramite.udai || 'N/A'}</p>
                                <p className="mb-0"><strong>Ingresó el:</strong> {tramite.fechaInicioTramiteAnses || 'N/A'}</p>
                            </>
                        )}

                        {/* DNI / Pacto / Carátula, igual que en el resto de las fichas. El "Pacto
                            OK" lo deja en verde solo el PACTO emitido desde el alta (ver
                            PactoDocumentoService.persistirPactoDelUltimo), que además carga los
                            haberes pactados que usa Cobranzas. */}
                        <ChecklistTramite tramite={tramite} onActualizado={recargarTramite} />

                        {/* Pacto de honorarios: lo rellena solo el PACTO emitido en el alta
                            (haberes y 30%) y se puede corregir acá hasta que nazca el cobro. */}
                        <TarjetaCobroTramite
                            tramite={tramite}
                            onPactoGenerado={() => Promise.all([recargarTramite(), recargarArchivos()])}
                        />
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold mb-3" style={{ color: '#6c757d' }}>Información del Cliente</h4>
                        <p><strong>CUIL:</strong> {cliente.cuil || 'N/A'}</p>
                        <p><strong>Nombre:</strong> {cliente.nombre || 'N/A'}</p>
                        <p><strong>Apellido:</strong> {cliente.apellido || 'N/A'}</p>
                        <p><strong>Teléfono:</strong> {cliente.telefonoPropio || 'N/A'}</p>
                        {cliente.cuil && (
                            <div className="mt-3 d-flex justify-content-center">
                                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                    onClick={() => verDetalleCliente(cliente.cuil)}>
                                    <PersonSearchIcon fontSize="medium" /> Ver Detalle del Cliente
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Seguimiento */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold mb-0" style={{ color: COLOR_OSCURO }}><i className="bi bi-signpost-split me-2"></i>Seguimiento</h4>
                            {!editSeguimiento && (
                                <button className="btn btn-sm rounded-pill px-3 fw-semibold" style={{ border: `1px solid ${COLOR_OSCURO}`, color: COLOR_OSCURO }} onClick={handleModificarSeguimiento}>
                                    <i className="bi bi-pencil me-1"></i>Editar
                                </button>
                            )}
                        </div>
                        {!editSeguimiento ? (
                            <div className="row g-2">
                                <div className="col-12"><h6 className="fw-bold text-secondary mb-1">Pronto despacho (Ley 23.789)</h6></div>
                                <div className="col-md-4"><strong>Fecha de envío:</strong> {tramite.fechaProntoDespacho || 'N/A'}</div>
                                <div className="col-md-4"><strong>N° de telegrama:</strong> {tramite.nroTelegrama || 'N/A'}</div>
                                <div className="col-md-4">
                                    <strong>Acuse:</strong>{' '}
                                    {tramite.acuseRecibido
                                        ? <><i className="bi bi-check-circle-fill text-success me-1"></i>Recibido{tramite.fechaAcuse ? ` (${tramite.fechaAcuse})` : ''}</>
                                        : <span className="text-muted">Pendiente</span>}
                                </div>
                                <div className="col-12"><hr className="my-2" /><h6 className="fw-bold text-secondary mb-1">Expediente judicial</h6></div>
                                <div className="col-md-6"><strong>Juzgado:</strong> {tramite.juzgado || 'N/A'}</div>
                                <div className="col-md-6"><strong>Expediente judicial:</strong> {tramite.nroExpedienteJudicial || 'N/A'}</div>
                                <div className="col-12"><strong>Carátula:</strong> {tramite.caratula || 'N/A'}</div>
                                <div className="col-12"><hr className="my-2" /><h6 className="fw-bold text-secondary mb-1">Hitos</h6></div>
                                <div className="col-md-4">{tramite.fechaAmparoPresentado ? <><i className="bi bi-check-circle-fill text-success me-1"></i>Presentado: {tramite.fechaAmparoPresentado}</> : <span className="text-muted">Presentación pendiente</span>}</div>
                                <div className="col-md-4">{tramite.fechaIntimacion ? <><i className="bi bi-check-circle-fill text-success me-1"></i>Intimación: {tramite.fechaIntimacion}</> : <span className="text-muted">Intimación pendiente</span>}</div>
                                <div className="col-md-4">{tramite.fechaPlazoFijado ? <><i className="bi bi-check-circle-fill text-success me-1"></i>Plazo fijado: {tramite.fechaPlazoFijado}</> : <span className="text-muted">Plazo pendiente</span>}</div>
                                <div className="col-md-4">{tramite.fechaResolucion ? <><i className="bi bi-check-circle-fill text-success me-1"></i>Resolución: {tramite.fechaResolucion}{tramite.nroResolucion ? ` (N° ${tramite.nroResolucion})` : ''}</> : <span className="text-muted">Resolución pendiente</span>}</div>
                                <div className="col-12"><hr className="my-2" /></div>
                                <div className="col-12"><strong>Tiempo comprometido:</strong> {tramite.tiempoComprometido || <span className="text-muted">Sin cargar (el escrito imprime el blanco a completar)</span>}</div>
                                <div className="col-12"><strong>Respuesta del organismo:</strong> {tramite.respuestaOrganismo || <span className="text-muted">Sin respuesta registrada (silencio)</span>}</div>
                                {tramite.motivoDesfavorable && <div className="col-12"><strong>Motivo desfavorable:</strong> {tramite.motivoDesfavorable}</div>}
                            </div>
                        ) : (
                            <>
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">N° expediente ANSES</label>
                                        <input type="text" className="form-control" value={seguimientoEdit.nroExpedienteAnses}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, nroExpedienteAnses: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">UDAI</label>
                                        <input type="text" className="form-control" value={seguimientoEdit.udai}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, udai: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">Ingreso a ANSES</label>
                                        <input type="date" className="form-control" value={seguimientoEdit.fechaInicioTramiteAnses}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, fechaInicioTramiteAnses: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">Fecha pronto despacho</label>
                                        <input type="date" className="form-control" value={seguimientoEdit.fechaProntoDespacho}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, fechaProntoDespacho: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">N° de telegrama</label>
                                        <input type="text" className="form-control" value={seguimientoEdit.nroTelegrama}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, nroTelegrama: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">Fecha del acuse</label>
                                        <input type="date" className="form-control" value={seguimientoEdit.fechaAcuse}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, fechaAcuse: e.target.value }))} />
                                        <div className="form-check mt-2">
                                            <input className="form-check-input" type="checkbox" id="chk-acuse"
                                                checked={!!seguimientoEdit.acuseRecibido}
                                                onChange={(e) => setSeguimientoEdit(p => ({ ...p, acuseRecibido: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="chk-acuse">Acuse recibido</label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Juzgado</label>
                                        <input type="text" className="form-control" value={seguimientoEdit.juzgado}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, juzgado: e.target.value }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">N° expediente judicial</label>
                                        <input type="text" className="form-control" value={seguimientoEdit.nroExpedienteJudicial}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, nroExpedienteJudicial: e.target.value }))} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label fw-semibold">Carátula</label>
                                        <input type="text" className="form-control" value={seguimientoEdit.caratula}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, caratula: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">Fecha de presentación</label>
                                        <input type="date" className="form-control" value={seguimientoEdit.fechaAmparoPresentado}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, fechaAmparoPresentado: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">Fecha de la intimación</label>
                                        <input type="date" className="form-control" value={seguimientoEdit.fechaIntimacion}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, fechaIntimacion: e.target.value }))} />
                                        <small className="text-muted">Recalcula los 5 días hábiles y mueve el evento de vencimiento.</small>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">Fecha límite para resolver</label>
                                        <input type="date" className="form-control" value={seguimientoEdit.fechaLimiteResolucion}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, fechaLimiteResolucion: e.target.value }))} />
                                        <small className="text-muted">Mueve el evento del plazo perentorio.</small>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label fw-semibold">Tiempo comprometido</label>
                                        <input type="text" className="form-control" value={seguimientoEdit.tiempoComprometido}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, tiempoComprometido: e.target.value }))} />
                                        <small className="text-muted">Es el renglón del escrito de amparo. Si queda vacío, sale el blanco a completar en Word.</small>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label fw-semibold">Respuesta del organismo (o silencio documentado)</label>
                                        <textarea className="form-control" rows={3} value={seguimientoEdit.respuestaOrganismo}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, respuestaOrganismo: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="d-flex gap-2 mt-3">
                                    <button className="btn rounded-pill px-4 fw-semibold text-white" style={{ backgroundColor: COLOR_OSCURO }} onClick={handleGuardarSeguimiento} disabled={guardandoSeguimiento}>
                                        {guardandoSeguimiento ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle me-2"></i>Guardar</>}
                                    </button>
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setEditSeguimiento(false)}>Cancelar</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Checklist de documentación */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <h4 className="fw-bold mb-3" style={{ color: COLOR_OSCURO }}><i className="bi bi-list-check me-2"></i>Checklist de documentación</h4>
                        {formularios.length === 0 ? (
                            <p className="text-muted fst-italic mb-0">No hay ítems de checklist.</p>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {formularios.map(f => (
                                    <li key={f.id} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2 px-0">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id={`chk-${f.id}`}
                                                checked={!!f.presentado} disabled={!!f.idArchivo}
                                                onChange={() => togglePresentado(f)} />
                                            <label className="form-check-label" htmlFor={`chk-${f.id}`}
                                                style={{ textDecoration: f.presentado ? 'line-through' : 'none', color: f.presentado ? '#6c757d' : 'inherit' }}>
                                                {f.nombre}
                                            </label>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {f.idArchivo ? (
                                                <>
                                                    <button className="btn btn-sm btn-outline-primary rounded-pill"
                                                        onClick={() => descargarPorId(f.idArchivo)} title={nombrePorId[f.idArchivo] || 'Descargar'}>
                                                        <i className="bi bi-download me-1"></i>{nombrePorId[f.idArchivo] || 'Descargar'}
                                                    </button>
                                                    <button className="btn btn-sm btn-link text-danger p-0" title="Quitar adjunto" onClick={() => quitarAdjuntoChecklist(f)}>
                                                        <i className="bi bi-x-circle"></i>
                                                    </button>
                                                </>
                                            ) : (
                                                <label className="btn btn-sm btn-outline-secondary rounded-pill mb-0">
                                                    {subiendoItem === f.id ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-paperclip me-1"></i>Adjuntar</>}
                                                    <input type="file" accept="application/pdf" hidden onChange={(e) => adjuntarArchivoChecklist(f, e.target.files[0])} />
                                                </label>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* Eventos del Trámite */}
            <div className="row mb-3 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold mb-0" style={{ color: COLOR_OSCURO }}>Eventos del Trámite</h4>
                            <button className="btn d-flex align-items-center gap-2 rounded-pill fw-semibold" style={{ border: `1px solid ${COLOR_OSCURO}`, color: COLOR_OSCURO }}
                                onClick={() => { const hoy = new Date(); setFechaSeleccionadaEvento(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())); setMostrarModalEvento(true); }}>
                                <span style={{ fontSize: '1.2rem' }}>+</span> Crear Evento
                            </button>
                        </div>
                        {cargandoEventos ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" style={{ color: COLOR }} role="status"></div>
                                <p className="mt-2 text-muted">Cargando eventos...</p>
                            </div>
                        ) : eventos.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center">
                                <p className="mb-0">No hay eventos registrados para este trámite</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {eventos.map(evento => (
                                    <div key={evento.idEvento} className="col-md-6 col-lg-4">
                                        <div className="card h-100 border rounded-3 p-3 shadow-sm">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="fw-bold mb-0" style={{ color: COLOR_OSCURO }}>{evento.titulo}</h6>
                                                {evento.tipoEvento && <span className="badge rounded-pill" style={{ backgroundColor: COLOR_OSCURO }}>{evento.tipoEvento}</span>}
                                            </div>
                                            {evento.descripcion && <p className="text-muted small mb-2" style={{ whiteSpace: 'pre-line' }}>{evento.descripcion}</p>}
                                            <div className="mt-auto">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <small className="text-muted">📅</small>
                                                    <small className="fw-semibold">{formatearFechaEvento(evento.fecha)}</small>
                                                </div>
                                                {evento.todoElDia ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small className="fw-semibold" style={{ color: COLOR_OSCURO }}>Todo el día</small>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small>{evento.horaInicio} - {evento.horaFin}</small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Otros documentos */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h4 className="fw-bold mb-0" style={{ color: COLOR_OSCURO }}><i className="bi bi-paperclip me-2"></i>Otros documentos</h4>
                            <label className="btn btn-sm rounded-pill mb-0" style={{ border: `1px solid ${COLOR_OSCURO}`, color: COLOR_OSCURO }}>
                                {subiendoGeneral ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-cloud-upload me-1"></i>Subir archivo</>}
                                <input type="file" hidden onChange={(e) => { subirGeneral(e.target.files[0]); e.target.value = ''; }} />
                            </label>
                        </div>
                        {archivosOtros.length === 0 ? (
                            <p className="text-muted fst-italic mb-0">No hay documentos cargados.</p>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {archivosOtros.map(a => (
                                    <div key={a.idArchivo} className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <i className="bi bi-file-earmark-text" style={{ fontSize: '1.25rem', color: COLOR_OSCURO }}></i>
                                            <div style={{ minWidth: 0 }}>
                                                <div className="fw-semibold text-truncate" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                    onClick={() => descargarPorId(a.idArchivo)} title={a.nombre}>
                                                    {a.nombre}
                                                </div>
                                                <small className="text-muted">{formatTamano(a.tamaño)}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            <button className="btn btn-sm" style={{ color: '#0056b3', border: 'none' }} title="Descargar" onClick={() => descargarPorId(a.idArchivo)}>
                                                <i className="bi bi-download"></i>
                                            </button>
                                            <button className="btn btn-sm" style={{ color: '#dc3545', border: 'none' }} title="Eliminar" onClick={() => eliminarGeneral(a.idArchivo)}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <CrearEventoModal
                isOpen={mostrarModalEvento}
                onClose={() => setMostrarModalEvento(false)}
                onSave={handleGuardarEvento}
                selectedDate={fechaSeleccionadaEvento}
                idTramite={idTramite}
            />

            <PanelAccionesAvanzadasTramite idTramite={tramite?.idTramite} tipoTramite="AMPARO" />

            <HistorialTramite
                isOpen={modalHistorialAbierto}
                onClose={() => setModalHistorialAbierto(false)}
                idTramite={idTramite}
                onEstadoCambiado={recargarTramite}
                ambitoFiltro="AMPARO"
            />

            <CustomModal {...modalState} onHide={hideModal} />
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
            <FeedbackButton tramite={tramite} />
        </div>
    );
}

export default VistaDetalladaTramiteAmparo;
